import React, { useMemo } from 'react';
import { connect, ConnectedProps } from 'react-redux';

import { RootState } from 'src/reducers';

import { logAnalyticsEvent } from 'Utilities/analytics/analyticsHelper';
import { getQuickShareCode, getQuickShareCodeLifetimeHours } from 'Utilities/formatHelper';
import { timeStampFormat } from 'Utilities/helpers';
import i18n from 'Utilities/i18n';
import mediator from 'Utilities/mediator';

import { ANALYTICS_ACTION_TYPE, ANAYLTICS_TITLE_TYPE } from 'Constants/analyticsEventTypes';

import LibreAppWrapper from 'Containers/LibreAppWrapper';

import HtmlTranslation from 'Components/HtmlTranslation';
import LoadingSpinner from 'Components/loading/LoadingSpinner';

import LVDSCodeLogoAR from 'Images/logos/LibreDataSharecode_Arabic.svg';
import LVDSCodeLogoHE from 'Images/logos/LibreDataSharecode_Hebrew.svg';
import LVDSCodeLogoJA from 'Images/logos/LibreDataSharecode_Japanese.svg';
import LVDSCodeLogoZH from 'Images/logos/LibreDataSharecode_SimplifiedChinese.svg';

import styles from './QuickShare.module.scss';

const stopSharing = (code: string | null) => {
  if (!code) {
    return;
  }

  mediator.publish('quickShare:stopSharingModal', {
    code,
    callback: (result: LVApiResponse) => {
      if (result.status === 0) {
        mediator.publish('modal:hide');
        mediator.publish('router:navigate', '/');

        logAnalyticsEvent({
          action: ANALYTICS_ACTION_TYPE.STOP_SHARING_BTN_CLICKED,
          eventTitle: ANAYLTICS_TITLE_TYPE.LDS_CODE_SHARING_STOPPED,
          code,
        });
      }
    },
  });
};

const backToHome = () => {
  mediator.publish('router:navigate', '/');
};

const mapState = ({
  user: {
    quickShare: { code, expiresAt },
    phoneLanguage,
  },
  config: { lv, quickShareCode: quickShareCodeConfig },
}: RootState) => {
  const lvUrl = lv.replace('https://', '').replace('http://', '');

  return {
    code,
    expiresAt,
    lvSharingUrl: `${lvUrl}/sharing`,
    phoneLanguage,
    quickShareCodeConfig,
  };
};

const connector = connect(mapState);

type Props = ConnectedProps<typeof connector>;

const QuickShare: React.FC<React.PropsWithChildren<Props>> = ({
  code,
  expiresAt,
  lvSharingUrl,
  phoneLanguage,
  quickShareCodeConfig,
}) => {
  const quickShareApp = useMemo(() => {
    const appConfig = {
      id: 'quickShare',
      logo: '',
      transGroupKey: 'QuickShare',
    };

    switch (phoneLanguage) {
      case 'ar-SA':
        appConfig.logo = LVDSCodeLogoAR;
        return appConfig;
      case 'zh-CN':
        appConfig.logo = LVDSCodeLogoZH;
        return appConfig;
      case 'ja-JP':
        appConfig.logo = LVDSCodeLogoJA;
        return appConfig;
      case 'he-IL':
        appConfig.logo = LVDSCodeLogoHE;
        return appConfig;
      default:
        return appConfig;
    }
  }, [phoneLanguage]);

  const codeFormatted = useMemo(
    () =>
      code && quickShareCodeConfig
        ? getQuickShareCode(code, quickShareCodeConfig.separator, quickShareCodeConfig.chunks)
        : null,
    [code, quickShareCodeConfig]
  );

  const codeLifetimeHours =
    quickShareCodeConfig && quickShareCodeConfig.lifetimeMinutes
      ? getQuickShareCodeLifetimeHours(quickShareCodeConfig.lifetimeMinutes)
      : 0;

  return (
    <LibreAppWrapper app={quickShareApp} appType="quickshare">
      <HtmlTranslation
        id="quickShareCode-active-text"
        className="line-height-xl mb-3"
        translationKey="QuickShare.generateCodeDesc"
        translationProps={{
          url: `<span class="text-primary">${lvSharingUrl}</span>`,
          number: codeLifetimeHours,
        }}
      />

      <div className={`${styles.code} text-center text-uppercase font-weight-bold mb-4`}>
        {code ? (
          codeFormatted
        ) : (
          <LoadingSpinner options={{ type: 'bars', height: '3rem', width: '3rem' }} />
        )}
      </div>

      {code && (
        <div className="mb-2 align-self-center">
          {i18n.t('QuickShare.expires', {
            date: timeStampFormat(expiresAt, 'MMMM D'),
            time: timeStampFormat(expiresAt, '', 'hourMinute'),
          })}
        </div>
      )}

      <button
        id="quickShareCode-stop-btn"
        className="mb-4 btn btn-link align-self-end"
        disabled={!code}
        onClick={() => stopSharing(code)}
      >
        {i18n.t('QuickShare.stopSharingNow', { context: 'cap' })}
      </button>

      <button
        id="quickShare-generate-btn"
        className="btn btn-lg btn-primary w-100"
        onClick={backToHome}
      >
        {i18n.t('Common.done', { context: 'cap' })}
      </button>
    </LibreAppWrapper>
  );
};

export default connector(QuickShare);
